import { cancelScheduledMail } from "@/api/ticket";
import clipMIcon from "@/assets/images/clip_m.png";
import { CustomMarkdown } from "@/components/CustomMarkdown";
import { Ticket, TicketComment } from "@/components/Ticket/types";
import { useCancelTimer } from "@/hooks/useCancelTimer";
import { useLoadingStore } from "@/stores/loading";
import { useUserStore } from "@/stores/user";
import { useEffect, useRef, useState } from "react";

interface Props {
  ticket?: Ticket;
  ticketComments?: TicketComment[];
  onCommentUpdated?: () => void;
}

export default function MessageList({ 
  ticket, 
  ticketComments,
  onCommentUpdated 
}: Props) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const comments = ticket ? ticket.data_ticket_comments : ticketComments ?? [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  return (
    <div className="messages">
      <table className="title-tbl">
        <tbody>
          <tr>
            <td className="label">問合せタイトル</td>
            <td className="content">{ticket?.inquiry_title}</td>
          </tr>
        </tbody>
      </table>
      {comments?.map((item, index) => (
        <Comment
          key={`frontend-ticket-message-list-${index}`}
          item={item}
          index={index}
          isInquiryUser={item.is_inquiry_user}
          ticket={ticket}
          onCommentUpdated={onCommentUpdated}
        />
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
}

interface CommentProps {
  ticket?: Ticket;
  item: TicketComment;
  index: number;
  isInquiryUser: boolean;
  onCommentUpdated?: () => void;
}

const Comment = ({ 
  ticket,
  item, 
  index,
  isInquiryUser,
  onCommentUpdated 
}: CommentProps) => {
  const user = useUserStore((state) => state.user);
  const setLoading = useLoadingStore((state) => state.setLoading);
  const [isCanceled, setIsCanceled] = useState(false);

  // 送信取消タイマー
  const { canCancel, remainingTimeText } = useCancelTimer({
    createdAt: item.created_with_seconds,
    onTimeUp: () => {
      // 時間切れ時の処理（特に何もしない）
    },
  });

  // 送信取消機能が必要かどうかを判定（お客様が送信したコメントで、自分が作成したもので、3分以内で、まだキャンセルされていない、かつ最初のメッセージでない）
  const isCreatedMe = user?.id == item.created_by;
  const isFirstMessage = index === 0;
  const needsCancelFunction = !!(
    isInquiryUser && 
    isCreatedMe && 
    canCancel && 
    !isCanceled && 
    !isFirstMessage
  );

  // 送信取消処理
  const handleCancelMail = async () => {
    if (!item.id) return;

    try {
      setLoading(true);
      await cancelScheduledMail({ data_ticket_comment_id: item.id });
      setIsCanceled(true);
      onCommentUpdated?.();
    } catch (error) {
      console.error("Failed to cancel mail:", error);
    } finally {
      setLoading(false);
    }
  };

  const messageType = isInquiryUser ? "sent" : "received";
  const group_name = item.display_created_group_by || item.created_group_by;
  const messageSentInfo = `${group_name} ${item.created_user_by}`;

  return (
    <div>
      <div className={`${messageType}-message`}>
        <div className={`${messageType}-message-name`}>{messageSentInfo}</div>
        <div className={`${messageType}-message-box`}>
          <div>
            <CustomMarkdown content={item.comment} />
          </div>
          {item.attached_files.length > 0 && (
            <>
              <hr />
              {item.attached_files.map((file, index) => (
                <div key={index} className="uk-flex uk-flex-middle">
                  <img
                    src={clipMIcon}
                    width={16}
                    height={16}
                    className="uk-margin-small-right"
                  />
                  <a href={file.filepath} target="_blank">
                    {file.original_filename}
                  </a>
                </div>
              ))}
            </>
          )}
        </div>
        <div className={`${messageType}-message-time`}>{item.created}</div>
        {needsCancelFunction ? (
          <div className="cancel-mail-section">
            <div className="cancel-mail-section-controls">
              <span className="cancel-mail-section-timer">
                {remainingTimeText}
              </span>
              <button
                onClick={handleCancelMail}
                className="uk-button--s uk-button-red cancel-mail-section-button"
              >
                送信取消
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
